// src/components/routing/ProtectedRoute.tsx
import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext.tsx';

interface ProtectedRouteProps {
  requiredPermissions?: string[];
  requireAll?: boolean; // true: tüm yetkiler gerekli, false: herhangi biri yeterli
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  requiredPermissions = [], 
  requireAll = false 
}) => {
  const { user, loading, profile } = useAuth();

  // Loading durumu - maksimum 10 saniye
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Yetki kontrol ediliyor...</span>
      </div>
    );
  }

  // Custom auth kontrolü
  const customToken = localStorage.getItem('custom_auth_token');
  const customUser = localStorage.getItem('custom_auth_user');
  let hasCustomAuth = false;
  if (customToken && customUser) {
    try {
      JSON.parse(customUser);
      hasCustomAuth = true;
    } catch (error) {
      localStorage.removeItem('custom_auth_token');
      localStorage.removeItem('custom_auth_user');
    }
  }

  // Temp session kontrolü
  const tempSession = localStorage.getItem('temp_user_session');
  let hasTempSession = false;
  if (tempSession) {
    try {
      const sessionData = JSON.parse(tempSession);
      hasTempSession = sessionData.expires_at > Date.now();
    } catch (error) {
      localStorage.removeItem('temp_user_session');
    }
  }

  // User yok ve custom auth yok ve temp session yok - login'e yönlendir
  if (!user && !hasCustomAuth && !hasTempSession) {
    console.log('🚫 ProtectedRoute: User yok, login\'e yönlendiriliyor');
    return <Navigate to="/auth/login" replace />;
  }
  
  // Profile yok ama custom auth veya temp session var - devam et
  if (!profile && !hasCustomAuth && !hasTempSession) {
    console.log('🚫 ProtectedRoute: Profile yok, login\'e yönlendiriliyor');
    return <Navigate to="/auth/login" replace />;
  }

  // Yetki kontrolü (sadece requiredPermissions varsa)
  if (requiredPermissions.length > 0 && profile?.permissions) {
    const hasPermission = (permission: string) => {
      return profile.permissions?.includes(permission) || false;
    };
    
    const hasRequiredPermissions = requireAll
      ? requiredPermissions.every(permission => hasPermission(permission))
      : requiredPermissions.some(permission => hasPermission(permission));

    if (!hasRequiredPermissions) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Erişim Reddedildi</h2>
            <p className="text-gray-600 mb-4">Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
            <button 
              onClick={() => window.history.back()}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Geri Dön
            </button>
          </div>
        </div>
      );
    }
  }

  return <Outlet />;
};

export default ProtectedRoute;
